import * as React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { favouritesAPIClient } from '@/infrastructure/favourites/api';

export function useFavouriteProductIdsQuery() {
  return useQuery({
    queryKey: ['favourite-products'],
    queryFn: () => favouritesAPIClient.getFavouriteProducts(),
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
}

export function useFavouriteProduct(productId: number | null, contextItemId: string) {
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data: favouriteProductIds } = useFavouriteProductIdsQuery();
  const strProductId = productId === null ? '' : productId.toString();

  const invalidateFavouriteProducts = () => {
    queryClient.invalidateQueries({ queryKey: ['favourite-products'] });
  };

  const isFavourite = favouriteProductIds?.includes(strProductId) ?? false;

  const addProductToFavouritesMutation = useMutation({
    mutationFn: () =>
      favouritesAPIClient.addProductToFavourites({
        contextItemId,
        productId: strProductId,
      }),
    onSuccess: invalidateFavouriteProducts,
  });

  const removeProductFromFavouritesMutation = useMutation({
    mutationFn: () =>
      favouritesAPIClient.removeProductFromFavourites({
        contextItemId,
        productId: strProductId,
      }),
    onSuccess: invalidateFavouriteProducts,
  });

  const toggleFavouriteState = React.useCallback(() => {
    if (!isFavourite) addProductToFavouritesMutation.mutate();
    if (isFavourite) removeProductFromFavouritesMutation.mutate();
  }, [addProductToFavouritesMutation, removeProductFromFavouritesMutation, isFavourite]);

  return {
    isLoading,
    isFetching,
    isFavourite,
    toggleFavouriteState,
  };
}
